import { CASE_STATUSES, datePl, type PossibleDuplicateCaseDto } from '@bqcrm/shared';
import { Alert, Anchor, Button, Group, Stack, Text } from '@mantine/core';
import { IconAlertTriangle, IconCopy } from '@tabler/icons-react';
import { useDebouncedValue } from '@mantine/hooks';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { api } from '../api';

/**
 * Ostrzeżenie o możliwym duplikacie (P8): otwarte sprawy tego samego klienta lub adresu ze wspólną branżą.
 * `onDuplicate` — przycisk „Zamknij jako duplikat” przy każdej sprawie (weryfikacja wstępna).
 */
export function PossibleDuplicatesAlert({ items, onDuplicate, newTab }: { items: PossibleDuplicateCaseDto[]; onDuplicate?: (id: string) => void; newTab?: boolean }) {
  if (!items.length) return null;
  return (
    <Alert mb="md" color="yellow" variant="light" icon={<IconAlertTriangle size={18} />} title={items.length === 1 ? 'Możliwy duplikat — klient lub adres ma już otwartą sprawę' : `Możliwe duplikaty — otwarte sprawy klienta lub adresu (${items.length})`}>
      <Stack gap={6}>
        {items.map((d) => (
          <Group key={d.id} gap="xs" justify="space-between" wrap="wrap">
            <Text size="sm" style={{ flex: '1 1 260px' }}>
              <Anchor component={Link} to={`/sprawy/${d.id}`} fw={700} size="sm" {...(newTab ? { target: '_blank' } : {})}>
                {d.number}
              </Anchor>{' '}
              · {d.title} · {CASE_STATUSES[d.status] ?? d.status}
              <Text span size="xs" c="dimmed">
                {' '}
                · {[d.sameClient ? 'ten sam klient' : d.clientName, d.sameSite ? 'ten sam adres' : d.address, d.trades.join(', '), `od ${datePl(d.createdAt.slice(0, 10))}`].filter(Boolean).join(' · ')}
              </Text>
            </Text>
            {onDuplicate && (
              <Button size="xs" variant="light" color="yellow" leftSection={<IconCopy size={14} />} onClick={() => onDuplicate(d.id)}>
                Zamknij jako duplikat
              </Button>
            )}
          </Group>
        ))}
      </Stack>
    </Alert>
  );
}

/** Formularz „Nowe zapytanie”: po wybraniu klienta — jego otwarte sprawy (lub sprawy pod tym adresem) w wybranych branżach. */
export function NewCaseDuplicates({ clientId, siteId, street, city, trades }: { clientId: string | null; siteId: string | null; street?: string; city?: string; trades: string[] }) {
  const qs = new URLSearchParams();
  if (clientId) qs.set('clientId', clientId);
  if (siteId) qs.set('siteId', siteId);
  if (!siteId && street?.trim() && city?.trim()) {
    qs.set('street', street.trim());
    qs.set('city', city.trim());
  }
  if (trades.length) qs.set('trades', trades.join(','));
  const [key] = useDebouncedValue(qs.toString(), 400);
  const q = useQuery({ queryKey: ['case-duplicates', key], queryFn: () => api<PossibleDuplicateCaseDto[]>(`/case-duplicates?${key}`), enabled: key.includes('clientId=') || key.includes('street=') });
  return <PossibleDuplicatesAlert items={q.data ?? []} newTab />;
}
